import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { parseError } from '../../../../lib/errors'
import { type COIDocument, uploadCOITemplate } from '../../../../models/coi'
import { selectionLabel } from './COIDocumentsTab'

/**
 * Replace one certificate with a PDF supplied by hand — used when the carrier
 * issues the document itself, or when a parse produced something wrong.
 */
export function UploadCOIDocumentDialog({
  document,
  orgId,
  onOpenChange,
  onSuccess,
}: {
  document: COIDocument | null
  orgId?: string
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)

  async function handleUpload() {
    if (!file || !document) return
    setUploading(true)
    try {
      await uploadCOITemplate(file, orgId, document.selectionKey)
      onSuccess()
      setFile(null)
      onOpenChange(false)
      toast.success('Certificate uploaded — Auto turned off for it')
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog
      open={!!document}
      onOpenChange={(open) => {
        if (!open) setFile(null)
        onOpenChange(open)
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Upload certificate</DialogTitle>
          {document && (
            <DialogDescription>
              Replaces the certificate for {selectionLabel(document)}. Holders on it are regenerated from the
              file you upload, its description of operations and remarks settings carry over, and Auto is
              switched off so a later policy change cannot overwrite it.
            </DialogDescription>
          )}
        </DialogHeader>
        <Dropzone onChange={setFile} accept=".pdf" maxSizeMB={10} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!file} loading={uploading ? 'Uploading…' : undefined}>
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
